import React, { useState } from "react";
import { addDays, formatDay, relativeDay } from "../lib/format";
import { todayISO } from "../../helpers/datetime";
import { post } from "../lib/api";
import { useStoredState } from "../lib/hooks";
import { Button, Input } from "../ui";
import { useShell } from "../shell/context";

export { DUE_VIEWS } from "../lib/lists";

export function LastTouchCell({ date }) {
    if (!date) return <span className="hz-muted hz-small">Never</span>;
    return (
        <div className="hz-nowrap">
            <div>{formatDay(date, { short: true })}</div>
            <div className="hz-xs hz-muted">{relativeDay(date)}</div>
        </div>
    );
}

// Client-side saved views (per browser). A view stores the built-in view it
// starts from plus the filters. Server-side views are a later decision.
export function useSavedViews(key) {
    const [views, setViews] = useStoredState(`hz-views-${key}`, []);
    const add = (view) => setViews((v) => [...v, { ...view, id: "v" + Date.now() }]);
    const remove = (id) => setViews((v) => v.filter((x) => x.id !== id));
    return { views, add, remove };
}

export function SaveViewButton({ onSave }) {
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState("");
    if (!editing) {
        return (
            <Button variant="outline" icon="ri-bookmark-line" onClick={() => setEditing(true)}>
                Save view
            </Button>
        );
    }
    const save = () => {
        if (!name.trim()) return;
        onSave(name.trim());
        setName("");
        setEditing(false);
    };
    return (
        <div className="hz-row" style={{ gap: 6 }}>
            <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") save();
                    if (e.key === "Escape") setEditing(false);
                }}
                placeholder="View name"
                style={{ width: 160 }}
            />
            <Button variant="primary" onClick={save}>Save</Button>
            <Button variant="light" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
    );
}

// Row actions shared by the People and Companies tables.
export function RowActions({ kind, row, onChanged }) {
    const { openLog, toast } = useShell();
    const base = kind === "company" ? "companies" : "contacts";
    const snooze = async () => {
        const until = addDays(todayISO(), 14);
        try {
            await post(`v1/crm/${base}/${row.id}/snooze`, { until });
            toast(`${row.name} snoozed until ${formatDay(until, { short: true })}.`, { icon: "ri-zzz-line" });
            onChanged();
        } catch (e) {
            toast(e.message, { error: true });
        }
    };
    const log = () =>
        openLog(kind === "company" ? { company: { id: row.id, name: row.name } } : { contact: { id: row.id, name: row.name, current_role: row.current_role } });
    return (
        <>
            <Button size="sm" variant="soft" iconOnly icon="ri-chat-new-line" title="Log touchpoint" onClick={log} />{" "}
            <Button size="sm" variant="light" iconOnly icon="ri-zzz-line" title="Snooze 2 weeks" onClick={snooze} />
        </>
    );
}
